import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, TextField, Grid, Alert, Box, Typography,
  Chip, IconButton, Stepper, Step, StepLabel, CircularProgress, Paper, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  type DocumentRecord,
  type EntityType,
  DOCUMENT_TYPES_BY_ENTITY,
  formatFileSize,
} from '../../constants/documentTypes';
import {
  DOCUMENT_ENTITY_MAP,
} from '../../constants/documentEntityConfig';
import { SearchableEntitySelect } from '../common/SearchableEntitySelect';
import { documentAPI } from '../../services/api';
import { catchyTextFieldSx } from '../../theme/formStyles';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';

interface DocumentUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  entityType?: EntityType;
  entityId?: string;
  entityLabel?: string;
  editDoc?: DocumentRecord | null;
  presetDocumentType?: string;
  /** Allow picking a vehicle (Documents page) */
  pickEntity?: boolean;
}

const STEPS = ['Select vehicle', 'Validity details', 'Upload file'];

function toDatetimeLocal(value?: string) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 16);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toDateInput(value?: string) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fileIcon(mime?: string, name?: string) {
  const isImage = mime?.startsWith('image/') || /\.(jpg|jpeg|png)$/i.test(name || '');
  return isImage
    ? <ImageOutlinedIcon sx={{ fontSize: 36, color: brandCss.primary }} />
    : <InsertDriveFileOutlinedIcon sx={{ fontSize: 36, color: brandCss.primary }} />;
}

export const DocumentUploadDialog: React.FC<DocumentUploadDialogProps> = ({
  open,
  onClose,
  onSaved,
  entityType: presetEntityType,
  entityId: presetEntityId,
  entityLabel: presetEntityLabel,
  editDoc,
  presetDocumentType,
  pickEntity = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState(0);
  const [entityType, setEntityType] = useState<EntityType>('vehicle');
  const [entityId, setEntityId] = useState('');
  const [entityLabel, setEntityLabel] = useState('');
  const [entityOptions, setEntityOptions] = useState<{ _id: string; label: string; sublabel?: string }[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [documentType, setDocumentType] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(editDoc);
  const showEntityPicker = pickEntity && !isEdit;
  const resolvedEntityType = (
    pickEntity ? 'vehicle' : (editDoc?.entityType || presetEntityType || 'vehicle')
  ) as EntityType;
  const resolvedEntityId = editDoc?.entityId || presetEntityId || entityId;
  const resolvedEntityLabel = presetEntityLabel || entityLabel;
  const entityConfig = DOCUMENT_ENTITY_MAP[resolvedEntityType];
  const typeOptions = DOCUMENT_TYPES_BY_ENTITY[resolvedEntityType] || [];
  const isVehicleDoc = resolvedEntityType === 'vehicle';
  const isDriverDoc = resolvedEntityType === 'driver';
  const isHelperDoc = resolvedEntityType === 'helper';
  const isPersonDoc = isDriverDoc || isHelperDoc;
  const isDriverDl = isPersonDoc && documentType === 'driving_license';
  const isSupportive = isVehicleDoc && documentType === 'supportive';
  const needsDates = isVehicleDoc && !isSupportive;
  const loadEntityOptions = useCallback(async (type: EntityType) => {
    const config = DOCUMENT_ENTITY_MAP[type];
    if (!config) return;
    setLoadingEntities(true);
    try {
      const options = await config.loadOptions();
      setEntityOptions(options);
      if (type === 'company' && options[0]) {
        setEntityId(options[0]._id);
        setEntityLabel(options[0].label);
      } else if (!options.some((o) => o._id === entityId)) {
        setEntityId('');
        setEntityLabel('');
      }
    } catch {
      setEntityOptions([]);
      setEntityId('');
      setEntityLabel('');
    } finally {
      setLoadingEntities(false);
    }
  }, [entityId]);

  useEffect(() => {
    if (!open) return;
    const initialType = pickEntity
      ? 'vehicle'
      : (editDoc?.entityType || presetEntityType || 'vehicle');
    setEntityType(initialType);
    setEntityId(editDoc?.entityId || presetEntityId || '');
    setEntityLabel(presetEntityLabel || '');
    setStep(showEntityPicker ? 0 : 1);

    if (editDoc) {
      setDocumentType(editDoc.documentType || '');
      setIssueDate(toDatetimeLocal(editDoc.issueDate));
      setExpiryDate(toDatetimeLocal(editDoc.expiryDate));
      setRemarks(editDoc.remarks || '');
    } else {
      const types = DOCUMENT_TYPES_BY_ENTITY[initialType] || [];
      setDocumentType(presetDocumentType || types[0]?.value || '');
      setIssueDate('');
      setExpiryDate('');
      setRemarks('');
    }
    setFile(null);
    setError('');
    setDragOver(false);

    if (showEntityPicker || (pickEntity && !presetEntityId)) {
      loadEntityOptions(initialType);
    }
  }, [open, editDoc, presetEntityType, presetEntityId, presetEntityLabel, presetDocumentType, showEntityPicker, pickEntity, loadEntityOptions]);

  useEffect(() => {
    if (!open || !showEntityPicker) return;
    loadEntityOptions(entityType);
  }, [entityType, open, showEntityPicker, loadEntityOptions]);

  const selectedEntityName = useMemo(() => {
    if (resolvedEntityLabel) return resolvedEntityLabel;
    return entityOptions.find((o) => o._id === resolvedEntityId)?.label || '';
  }, [resolvedEntityLabel, entityOptions, resolvedEntityId]);

  const canGoNext = useMemo(() => {
    if (step === 0) return Boolean(resolvedEntityId);
    if (step === 1) {
      if (!documentType) return false;
      if (isDriverDl) return Boolean(expiryDate);
      if (isPersonDoc) return true;
      if (isSupportive) {
        if (issueDate && expiryDate && new Date(expiryDate) <= new Date(issueDate)) return false;
        return true;
      }
      if (isVehicleDoc) {
        if (!issueDate || !expiryDate) return false;
        if (new Date(expiryDate) <= new Date(issueDate)) return false;
        return true;
      }
      return true;
    }
    return true;
  }, [step, resolvedEntityId, documentType, issueDate, expiryDate, isVehicleDoc, isPersonDoc, isDriverDl, isSupportive]);

  const handleEntityChange = (id: string) => {
    setEntityId(id);
    const match = entityOptions.find((o) => o._id === id);
    setEntityLabel(match?.label || '');
  };

  const acceptFile = (next: File | null) => {
    if (!next) {
      setFile(null);
      return;
    }
    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowed.includes(next.type) && !/\.(pdf|jpe?g|png)$/i.test(next.name)) {
      setError('Only PDF, JPG, and PNG files are allowed');
      return;
    }
    setError('');
    setFile(next);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    acceptFile(e.dataTransfer.files?.[0] || null);
  };

  const handleSubmit = async () => {
    if (!documentType) {
      setError('Document type is required');
      setStep(1);
      return;
    }
    if (!resolvedEntityId) {
      setError('Please select a vehicle');
      setStep(0);
      return;
    }
    if (isVehicleDoc) {
      if (!isSupportive) {
        if (!issueDate || !expiryDate) {
          setError('Enter issue and validity date and time');
          setStep(1);
          return;
        }
        if (new Date(expiryDate) <= new Date(issueDate)) {
          setError('Validity / expiry date and time must be after the issue date and time');
          setStep(1);
          return;
        }
      } else if (issueDate && expiryDate && new Date(expiryDate) <= new Date(issueDate)) {
        setError('Validity / expiry date and time must be after the issue date and time');
        setStep(1);
        return;
      }
      if (!editDoc && !file) {
        setError('Please upload the document file');
        setStep(2);
        return;
      }
    }

    if (isDriverDl) {
      if (!expiryDate) {
        setError('Enter Valid till date for Driving License');
        setStep(1);
        return;
      }
      if (!editDoc && !file) {
        setError('Please upload the document file');
        setStep(2);
        return;
      }
    }

    if (isPersonDoc && !isDriverDl && !editDoc && !file) {
      setError('Please upload the document file');
      setStep(2);
      return;
    }

    const formData = new FormData();
    formData.append('entityType', resolvedEntityType);
    formData.append('entityId', resolvedEntityId);
    formData.append('documentType', documentType);
    if (issueDate && isVehicleDoc) formData.append('issueDate', issueDate);
    if (expiryDate && (isVehicleDoc || isDriverDl)) formData.append('expiryDate', expiryDate);
    if (remarks) formData.append('remarks', remarks);
    if (file) formData.append('file', file);

    try {
      setSaving(true);
      setError('');
      if (editDoc) {
        await documentAPI.update(editDoc._id, formData);
      } else {
        await documentAPI.upload(formData);
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const renderEntityStep = () => (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        Select the vehicle for this document
      </Typography>
      {loadingEntities ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">Loading vehicles…</Typography>
        </Box>
      ) : entityOptions.length === 0 ? (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          No vehicles found. Add a vehicle first, then upload RC, fitness, insurance and other vehicle papers.
        </Alert>
      ) : (
        <SearchableEntitySelect
          label="Search vehicle"
          placeholder="Type vehicle number…"
          value={entityId}
          options={entityOptions}
          onChange={handleEntityChange}
          required
        />
      )}
    </Box>
  );

  const renderDetailsStep = () => (
    <Box>
      {!showEntityPicker && (presetEntityLabel || selectedEntityName) && (
        <Chip
          label={`${entityConfig?.label}: ${selectedEntityName}`}
          sx={{ mb: 2, fontWeight: 600, bgcolor: `${brandCss.primary}12`, color: brandCss.primaryDark }}
        />
      )}

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Document type
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
        {typeOptions.map((t) => (
          <Chip
            key={t.value}
            label={t.label}
            clickable
            onClick={() => {
              setDocumentType(t.value);
              if (t.value !== 'driving_license') setExpiryDate('');
            }}
            sx={{
              height: 30,
              fontWeight: documentType === t.value ? 700 : 500,
              bgcolor: documentType === t.value ? brandCss.primary : '#f1f5f9',
              color: documentType === t.value ? '#fff' : brandColors.textDark,
            }}
          />
        ))}
      </Box>

      <Grid container spacing={2}>
        {isVehicleDoc && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required={needsDates}
                type="datetime-local"
                label="Issue date & time"
                InputLabelProps={{ shrink: true }}
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                sx={catchyTextFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required={needsDates}
                type="datetime-local"
                label="Valid until (expiry date & time)"
                InputLabelProps={{ shrink: true }}
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                sx={catchyTextFieldSx}
                helperText={isSupportive ? 'Optional for supportive documents' : 'Must be after the issue date and time'}
              />
            </Grid>
          </>
        )}
        {isDriverDl && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="date"
              label="Valid till"
              InputLabelProps={{ shrink: true }}
              value={toDateInput(expiryDate) || expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              sx={catchyTextFieldSx}
              helperText="Driving License expiry date"
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField fullWidth label="Remarks (optional)" multiline rows={2} value={remarks}
            onChange={(e) => setRemarks(e.target.value)} sx={catchyTextFieldSx}
            placeholder={
              isPersonDoc
                ? 'Optional note'
                : (isSupportive ? 'Describe this supportive document' : undefined)
            }
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderUploadStep = () => (
    <Box>
      <Paper
        elevation={0}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          p: 3,
          borderRadius: 2,
          border: `2px dashed ${dragOver ? brandCss.primary : brandColors.border}`,
          bgcolor: dragOver ? `${brandCss.primary}08` : '#fafafa',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          '&:hover': { borderColor: brandCss.primary, bgcolor: `${brandCss.primary}05` },
        }}
      >
        <input
          ref={fileInputRef}
          hidden
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          onChange={(e) => acceptFile(e.target.files?.[0] || null)}
        />
        {file || editDoc?.fileName ? (
          <Box>
            {fileIcon(file?.type || editDoc?.mimeType, file?.name || editDoc?.fileName)}
            <Typography fontWeight={700} sx={{ mt: 1 }}>
              {file?.name || editDoc?.fileName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {file ? formatFileSize(file.size) : editDoc?.sizeBytes ? formatFileSize(editDoc.sizeBytes) : 'Existing file'}
            </Typography>
            {file && (
              <Button size="small" sx={{ mt: 1 }} onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                Remove
              </Button>
            )}
          </Box>
        ) : (
          <Box>
            <CloudUploadIcon sx={{ fontSize: 48, color: brandCss.primary, mb: 1 }} />
            <Typography fontWeight={700}>Upload the document file</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              PDF, JPG or PNG{isPersonDoc || isVehicleDoc ? ' — file required' : ''}
            </Typography>
          </Box>
        )}
      </Paper>

      {!editDoc && (
        <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: `1px solid ${brandColors.border}` }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
            Summary
          </Typography>
          <Typography fontSize={13}>
            <strong>{typeOptions.find((t) => t.value === documentType)?.label || 'Document'}</strong>
            {' · '}
            {entityConfig?.label}: {selectedEntityName || '—'}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: `${RADIUS}px`, overflow: 'hidden' } }}
    >
      <Box sx={{
        px: 3,
        py: 2,
        background: brandCss.gradientPrimary,
        color: '#fff',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
      }}>
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 1 }}>
            {isEdit ? 'Update' : 'New upload'}
          </Typography>
          <Typography variant="h6" fontWeight={800}>
            {isEdit ? 'Update Document' : 'Upload Document'}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.25 }}>
            {isEdit
              ? (isPersonDoc ? 'Replace file or update remarks' : 'Replace file or update validity date and time')
              : isPersonDoc
                ? 'Upload DL (with Valid till), Aadhaar Front and Aadhaar Back — no memo needed'
                : 'Upload RC, fitness, insurance, PUC, permits, tax or FASTag for a vehicle'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff', mt: -0.5, mr: -1 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {showEntityPicker && (
          <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {showEntityPicker && step === 0 && renderEntityStep()}
        {((showEntityPicker && step === 1) || (!showEntityPicker && step >= 1)) && renderDetailsStep()}
        {showEntityPicker && step === 2 && (
          <>
            <Divider sx={{ my: 2 }} />
            {renderUploadStep()}
          </>
        )}
        {!showEntityPicker && (
          <>
            <Divider sx={{ my: 2 }} />
            {renderUploadStep()}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8fafc', borderTop: `1px solid ${brandColors.border}` }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        {showEntityPicker && step > 0 && (
          <Button onClick={() => setStep((s) => s - 1)} disabled={saving}>Back</Button>
        )}
        {showEntityPicker && step < 2 ? (
          <Button
            variant="contained"
            disabled={!canGoNext || saving || (step === 0 && !entityId)}
            onClick={() => setStep((s) => s + 1)}
            sx={{ fontWeight: 700, background: brandCss.gradientPrimary, minWidth: 100 }}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              saving
              || (needsDates && (!issueDate || !expiryDate))
              || (isDriverDl && !expiryDate)
              || (!editDoc && !file)
            }
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
            sx={{ fontWeight: 700, background: brandCss.gradientPrimary, minWidth: 120 }}
          >
            {saving ? 'Saving…' : isEdit ? 'Update' : 'Upload'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
